import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { TOUCH_TARGET_PX } from '../config/touch-target';

const SEGMENT_SX = {
  minHeight: TOUCH_TARGET_PX,
  minWidth: TOUCH_TARGET_PX,
  px: 2,
  textTransform: 'none',
  fontWeight: 600,
} as const;

export interface SegmentedSwitchOption<Value extends string> {
  value: Value;
  label: string;
  testId?: string;
}

interface SegmentedSwitchProps<Value extends string> {
  value: Value;
  options: readonly SegmentedSwitchOption<Value>[];
  onChange: (value: Value) => void;
  /** Accessible name of the group, when no visible label points at it. */
  ariaLabel?: string;
  /** Id of the visible label describing the group. */
  ariaLabelledBy?: string;
  /** Id of the helper text that explains the current choice. */
  ariaDescribedBy?: string;
  fullWidth?: boolean;
  testId?: string;
}

/** Two-or-more segment exclusive switch (same look as the calendar "Month / Week" switch). */
export function SegmentedSwitch<Value extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  fullWidth,
  testId,
}: SegmentedSwitchProps<Value>) {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      fullWidth={fullWidth}
      value={value}
      onChange={(_event, next: Value | null) => next && onChange(next)}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      data-testid={testId}
    >
      {options.map((option) => (
        <ToggleButton
          key={option.value}
          value={option.value}
          data-testid={option.testId}
          sx={SEGMENT_SX}
        >
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
